import type { RecordEntityId, ReportingConfig, StationBrand } from '@centric-os/types';
import type { AxiosResponse } from 'axios';
import { defineStore, type StateTree } from 'pinia';
import { useReportConfigMapper } from '../../../composables';

interface State extends StateTree {
  reportingConfig: ReportingConfig | null;
  reportingConfigDefault: ReportingConfig | null;
  isDayEdit: boolean;
  isDayPartsEdit: boolean;
  isFormDirty: boolean;
}

export function useReportingConfigStore(storeId = 'reportingConfig') {
  const store = defineStore(storeId, {
    state: (): State => ({
      reportingConfig: null,
      reportingConfigDefault: null,
      isDayEdit: false,
      isDayPartsEdit: false,
      isFormDirty: false,
    }),
    actions: {
      async getReportingConfig(location_group_id: RecordEntityId): Promise<void> {
        const { mapReportConfigResponse } = useReportConfigMapper();

        const response = await this.cdlApi.get<ReportingConfig>(
          `/centricos/report/group/${location_group_id}/config`,
        );
        const { data } = response;
        this.reportingConfig = mapReportConfigResponse(data);
      },
      async getReportingConfigDefault(location_group_id: RecordEntityId): Promise<void> {
        const { mapReportConfigResponse } = useReportConfigMapper();

        const response = await this.cdlApi.get<ReportingConfig>(
          `/centricos/report/group/${location_group_id}/config/default`,
        );
        const { data } = response;
        data.location_group_id = location_group_id;
        data.is_default = true;
        this.reportingConfigDefault = mapReportConfigResponse(data);
      },
      async updateReportingConfig(payload: ReportingConfig) {
        const { mapReportConfigPayload, mapReportConfigResponse } = useReportConfigMapper();

        if (payload && payload.location_group_id) {
          const response = await this.cdlApi.put<
            ReportingConfig,
            AxiosResponse<ReportingConfig, any>,
            Partial<ReportingConfig>
          >(`/centricos/report/group/${payload.location_group_id}/config`, {
            ...mapReportConfigPayload(payload),
          });

          const { data } = response;
          this.reportingConfig = mapReportConfigResponse(data);
        } else {
          return Promise.reject(new Error('no category selected'));
        }
      },
      async deleteCustomConfig(location_group_id: RecordEntityId) {
        const { mapReportConfigResponse } = useReportConfigMapper();
        const response = await this.cdlApi.delete<
          ReportingConfig,
          AxiosResponse<ReportingConfig, any>,
          ReportingConfig
        >(`/centricos/report/group/${location_group_id}/config`);

        const { data } = response;
        this.reportingConfig = mapReportConfigResponse(data);
      },
    },
    getters: {
      isReportingConfigCustom(state: State): boolean {
        return !state.reportingConfig?.is_default;
      },
    },
    pagination: false,
  });
  return store();
}
